/**
 * Selection-модель для мультиэдита (совместима с QueryTable).
 *
 * Состояние "headerCheckbox":
 * - 'none', выбраны только сущности из selectedEntities[].
 * - 'all', выбраны ВСЕ, кроме сущностей из unselectedEntities[].
 * - 'many', частичный выбор (когда часть сущностей закреплена в pinned).
 *
 * Мутируем входящие массивы in-place, потому что родитель делает
 * `selectedEntities = reactive(val)` и эмитит назад через update:*.
 */

import { computed, type Ref } from "vue";

export interface UseMultiEditStateArgs {
  selectedEverything: Ref<string>;
  selectedEntities: Ref<number[]>;
  unselectedEntities: Ref<number[]>;
  emitSelected: (val: number[]) => void;
  emitUnselected: (val: number[]) => void;
  emitSelectedAll: (val: string) => void;
  emitSelectionChangeManual: () => void;
}

export function useMultiEditState(args: UseMultiEditStateArgs) {
  const headerCheckbox = computed<string>({
    get: () => args.selectedEverything.value || "none",
    set: (v: string) => args.emitSelectedAll(v),
  });

  function entityChecked(entityId: number): boolean {
    return headerCheckbox.value === "none"
      ? (args.selectedEntities.value || []).includes(entityId)
      : !(args.unselectedEntities.value || []).includes(entityId);
  }

  function updateSelection(checked: boolean, entityId: number) {
    if (headerCheckbox.value === "none") {
      const sel = args.selectedEntities.value.slice();
      const idx = sel.indexOf(entityId);
      if (checked) {
        if (idx === -1) sel.push(entityId);
      } else if (idx >= 0) {
        sel.splice(idx, 1);
      }
      args.emitSelected(sel);
    } else {
 // Режим "selected all", храним исключения в unselectedEntities.
      const unsel = args.unselectedEntities.value.slice();
      const idx = unsel.indexOf(entityId);
      if (!checked) {
        if (idx === -1) unsel.push(entityId);
      } else if (idx >= 0) {
        unsel.splice(idx, 1);
      }
      args.emitUnselected(unsel);
    }
    args.emitSelectionChangeManual();
  }

  function setHeaderCheckbox(value: "none" | "all") {
    args.emitSelectedAll(value);
    if (value === "all") {
      args.emitUnselected([]);
    } else {
      args.emitSelected([]);
    }
  }

  return {
    headerCheckbox,
    entityChecked,
    updateSelection,
    setHeaderCheckbox,
  };
}
